import React, { useCallback, useEffect, useState } from "react";
import { Toaster, toast } from "sonner";
import { BarChart3, Percent, RefreshCw, Swords, Users } from "lucide-react";
import { Brand } from "@/components/Brand";
import { fetchPublicTrystomaticTrack } from "@/lib/api";
import { fmtNum } from "@/lib/format";
import { TradeCard } from "@/components/LeagueTrades";

const REFRESH_MS = 45_000;
const PUBLIC_USER = "TrystonEvjen";

export function PublicTrystomaticTrack({ embedded = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [leagueIdx, setLeagueIdx] = useState(0);

  const load = useCallback(async (silent) => {
    if (!silent) setBusy(true);
    try {
      const d = await fetchPublicTrystomaticTrack();
      setData(d);
      setLeagueIdx((i) => {
        const n = d?.leagues?.length || 0;
        if (!n) return 0;
        return Math.min(i, n - 1);
      });
    } catch {
      toast.error("Could not load public league dashboard.");
    } finally {
      setLoading(false);
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    load(false);
  }, [load]);

  useEffect(() => {
    const id = window.setInterval(() => load(true), REFRESH_MS);
    return () => window.clearInterval(id);
  }, [load]);

  const league = data?.leagues?.[leagueIdx] || null;
  const MainTag = embedded ? "div" : "main";
  const mainClass = embedded ? "space-y-10" : "max-w-7xl mx-auto px-6 lg:px-12 py-10 space-y-10";

  return (
    <div className={embedded ? "" : "min-h-screen bg-white"} data-testid="public-trystomatic-track">
      {!embedded && <Toaster position="top-right" richColors />}
      {embedded ? (
        <div className="flex flex-wrap items-end justify-between gap-4 border-b border-[#0A0A0A]/15 pb-6 mb-2">
          <div>
            <div className="label-eyebrow">Public dashboard</div>
            <p className="text-sm text-[#52525B] mt-1 max-w-2xl">
              Live Sleeper for <span className="font-mono-data text-[#0A0A0A]">@{PUBLIC_USER}</span>
              {" "}· snapshot refreshes every {REFRESH_MS / 1000}s.{" "}
              <a href="/track/trystomatic-leagues" className="text-[#7C3AED] hover:underline font-medium">
                Full-page link
              </a>
            </p>
          </div>
          <button
            type="button"
            onClick={() => load(false)}
            disabled={busy}
            className="brutal-btn-outline px-4 py-2.5 text-xs uppercase tracking-widest inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="public-track-refresh"
          >
            <RefreshCw size={14} className={busy ? "animate-spin" : ""} /> Refresh now
          </button>
        </div>
      ) : (
        <header className="border-b border-[#0A0A0A] bg-white sticky top-0 z-40">
          <div className="max-w-7xl mx-auto px-6 lg:px-12 py-4 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="flex items-start gap-4">
              <Brand size="md" />
              <div>
                <div className="label-eyebrow">Public dashboard</div>
                <h1 className="font-display font-black text-2xl sm:text-3xl tracking-tighter mt-0.5">
                  Track Trystomatic Leagues
                </h1>
                <p className="text-sm text-[#52525B] mt-1 max-w-xl">
                  Live Sleeper data for <span className="font-mono-data text-[#0A0A0A]">@{PUBLIC_USER}</span>
                  {" "}— no sign-in. Refreshes every {REFRESH_MS / 1000}s and when you click refresh.
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => load(false)}
                disabled={busy}
                className="brutal-btn-outline px-4 py-2.5 text-xs uppercase tracking-widest inline-flex items-center gap-2 disabled:opacity-50"
                data-testid="public-track-refresh"
              >
                <RefreshCw size={14} className={busy ? "animate-spin" : ""} /> Refresh now
              </button>
              <a
                href="/"
                className="bg-[#7C3AED] text-white border border-[#0A0A0A] px-4 py-2.5 text-xs uppercase tracking-widest font-semibold hover:bg-[#0A0A0A] transition-colors"
                data-testid="public-track-sign-in"
              >
                Open TRYSTOMATIC
              </a>
            </div>
          </div>
        </header>
      )}

      <MainTag className={mainClass}>
        {loading && !data ? (
          <div className="py-24 text-center text-[#52525B] text-sm font-mono-data">Loading leagues…</div>
        ) : !data?.leagues?.length ? (
          <div className="brutal-card p-8 text-center text-[#52525B]">
            No leagues found for @{PUBLIC_USER} in season {data?.season || "—"}.
          </div>
        ) : (
          <>
            <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <div className="brutal-card px-4 py-3">
                <div className="label-eyebrow">Sleeper season</div>
                <div className="font-display font-black text-2xl tabular-nums">{data.season}</div>
              </div>
              <div className="brutal-card px-4 py-3">
                <div className="label-eyebrow">NFL week (matchups)</div>
                <div className="font-display font-black text-2xl tabular-nums">{data.nfl_week}</div>
              </div>
              <div className="brutal-card px-4 py-3">
                <div className="label-eyebrow">Leagues tracked</div>
                <div className="font-display font-black text-2xl tabular-nums">{data.leagues.length}</div>
              </div>
              <div className="brutal-card px-4 py-3">
                <div className="label-eyebrow">Last updated</div>
                <div className="font-mono-data text-xs text-[#52525B] mt-1">
                  {data.updated_at ? new Date(data.updated_at).toLocaleString() : "—"}
                </div>
              </div>
            </section>

            {data.my_cross_league_ownership?.length > 0 && data.my_leagues_count > 0 && (
              <section className="brutal-card p-5 overflow-hidden border-2 border-[#0A0A0A]">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <Percent size={18} />
                  <h2 className="font-display font-black text-xl sm:text-2xl tracking-tight">
                    Portfolio ownership (all @{PUBLIC_USER} leagues)
                  </h2>
                </div>
                <p className="text-sm text-[#52525B] mb-4 max-w-3xl">
                  Each player is keyed by Sleeper ID across leagues. <strong className="text-[#0A0A0A]">Portfolio %</strong> is how many tracked leagues you roster them in (sole owner or co-owner) — e.g. 1 of 2 leagues = 50%.
                </p>
                <div className="text-[11px] uppercase tracking-widest text-[#52525B] font-mono-data mb-3">
                  Denominator: {data.my_leagues_count} league{data.my_leagues_count === 1 ? "" : "s"} on this page where @{PUBLIC_USER} has a roster (including co-owned teams)
                </div>
                <div className="overflow-x-auto max-h-[min(70vh,560px)] overflow-y-auto">
                  <table className="w-full text-sm border-collapse min-w-[480px]">
                    <thead className="sticky top-0 bg-white z-10 border-b border-[#0A0A0A]/20 text-left text-[10px] uppercase tracking-widest text-[#52525B] font-mono-data">
                      <tr>
                        <th className="py-2 pr-3">Player</th>
                        <th className="py-2 pr-3 text-right">Leagues</th>
                        <th className="py-2 pr-3 text-right">Portfolio %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.my_cross_league_ownership.map((row) => (
                        <tr key={row.player_id} className="border-b border-[#0A0A0A]/10">
                          <td className="py-2 pr-3 font-medium">{row.name}</td>
                          <td className="py-2 pr-3 text-right font-mono-data tabular-nums">
                            {row.leagues_owned} / {row.leagues_total}
                          </td>
                          <td className="py-2 pr-3 text-right font-mono-data tabular-nums font-semibold">{row.pct}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}

            {data.my_recent_trades_all?.length > 0 && (
              <section>
                <div className="flex items-end justify-between gap-3 flex-wrap mb-4">
                  <div>
                    <span className="label-eyebrow">Recent trades · @{PUBLIC_USER}</span>
                    <h2 className="font-display font-black text-2xl sm:text-3xl tracking-tighter mt-1">
                      Across all leagues.
                    </h2>
                    <p className="text-sm text-[#52525B] mt-1 max-w-2xl">
                      Completed Sleeper trades involving Tryst&apos;s roster, valued the same way as inside TRYSTOMATIC.
                    </p>
                  </div>
                </div>
                <div className="space-y-4">
                  {data.my_recent_trades_all.map((t) => (
                    <TradeCard key={t.transaction_id} trade={t} />
                  ))}
                </div>
              </section>
            )}

            <section>
              <div className="label-eyebrow mb-2">Select league</div>
              <div className="flex gap-2 overflow-x-auto pb-2 border-b border-[#0A0A0A]/10">
                {data.leagues.map((lg, i) => (
                  <button
                    key={lg.league_id}
                    type="button"
                    onClick={() => setLeagueIdx(i)}
                    className={`shrink-0 px-4 py-2 text-xs uppercase tracking-widest border border-[#0A0A0A] transition-colors ${
                      i === leagueIdx ? "bg-[#0A0A0A] text-white" : "bg-white text-[#0A0A0A] hover:bg-[#F3F4F6]"
                    }`}
                    data-testid={`public-league-tab-${lg.league_id}`}
                  >
                    {lg.name}
                  </button>
                ))}
              </div>
            </section>

            {league && (
              <>
                <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="brutal-card p-5">
                    <div className="flex items-center gap-2 mb-4">
                      <Swords size={18} />
                      <h3 className="font-display font-black text-xl tracking-tight">This week&apos;s matchups</h3>
                      <span className="text-[10px] uppercase tracking-widest text-[#52525B] font-mono-data ml-auto">
                        Week {league.nfl_matchup_week}
                      </span>
                    </div>
                    {!league.matchups?.length ? (
                      <p className="text-sm text-[#52525B]">No matchup rows for this week yet (off-season or pre-kickoff).</p>
                    ) : (
                      <div className="space-y-4">
                        {league.matchups.map((m) => (
                          <div key={String(m.matchup_id)} className="border border-[#0A0A0A]/15 p-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              {(m.teams || []).map((t) => (
                                <div key={t.roster_id} className="flex items-center justify-between gap-3">
                                  <div className="min-w-0">
                                    <div className="font-semibold truncate">{t.team_name}</div>
                                    <div className="text-[11px] text-[#52525B] font-mono-data">Roster {t.roster_id}</div>
                                  </div>
                                  <div className="font-display font-black text-2xl tabular-nums">{fmtNum(t.points)}</div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="brutal-card p-5">
                    <div className="flex items-center gap-2 mb-4">
                      <BarChart3 size={18} />
                      <h3 className="font-display font-black text-xl tracking-tight">League analytics</h3>
                      <span className="text-[10px] uppercase tracking-widest text-[#52525B] font-mono-data ml-auto">
                        {league.format}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="border border-[#0A0A0A]/10 p-3">
                        <div className="label-eyebrow">Avg team value</div>
                        <div className="font-mono-data font-bold text-lg tabular-nums">{fmtNum(league.analytics?.avg_team_value)}</div>
                      </div>
                      <div className="border border-[#0A0A0A]/10 p-3">
                        <div className="label-eyebrow">Value spread</div>
                        <div className="font-mono-data font-bold text-lg tabular-nums">{fmtNum(league.analytics?.value_spread)}</div>
                      </div>
                      <div className="border border-[#0A0A0A]/10 p-3">
                        <div className="label-eyebrow">Highest roster</div>
                        <div className="font-mono-data font-bold text-lg tabular-nums">{fmtNum(league.analytics?.max_team_value)}</div>
                      </div>
                      <div className="border border-[#0A0A0A]/10 p-3">
                        <div className="label-eyebrow">Lowest roster</div>
                        <div className="font-mono-data font-bold text-lg tabular-nums">{fmtNum(league.analytics?.min_team_value)}</div>
                      </div>
                    </div>
                  </div>
                </section>

                <section className="brutal-card p-5 overflow-hidden">
                  <div className="flex items-center gap-2 mb-4">
                    <Users size={18} />
                    <h3 className="font-display font-black text-xl tracking-tight">Standings &amp; records</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm border-collapse min-w-[640px]">
                      <thead>
                        <tr className="border-b border-[#0A0A0A]/20 text-left text-[10px] uppercase tracking-widest text-[#52525B] font-mono-data">
                          <th className="py-2 pr-2">#</th>
                          <th className="py-2 pr-2">Team</th>
                          <th className="py-2 pr-2 text-right">W</th>
                          <th className="py-2 pr-2 text-right">L</th>
                          <th className="py-2 pr-2 text-right">PF</th>
                          <th className="py-2 pr-2 text-right">TRYST value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {league.standings?.map((row, idx) => (
                          <tr key={row.roster_id} className="border-b border-[#0A0A0A]/10">
                            <td className="py-2 pr-2 font-mono-data text-[#52525B]">{idx + 1}</td>
                            <td className="py-2 pr-2 font-semibold">
                              {row.team_name}
                              {row.roster_id === league.my_roster_id && (
                                <span className="ml-2 text-[10px] uppercase tracking-widest text-[#7C3AED] font-mono-data">Tryst</span>
                              )}
                            </td>
                            <td className="py-2 pr-2 text-right font-mono-data tabular-nums">{row.wins}</td>
                            <td className="py-2 pr-2 text-right font-mono-data tabular-nums">{row.losses}</td>
                            <td className="py-2 pr-2 text-right font-mono-data tabular-nums">{fmtNum(row.fpts)}</td>
                            <td className="py-2 pr-2 text-right font-mono-data tabular-nums">{fmtNum(row.total_value)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>

                {league.my_recent_trades?.length > 0 && (
                  <section>
                    <div className="mb-4">
                      <span className="label-eyebrow">This league only</span>
                      <h3 className="font-display font-black text-2xl tracking-tighter mt-1">Tryst&apos;s recent trades</h3>
                    </div>
                    <div className="space-y-4">
                      {league.my_recent_trades.map((t) => (
                        <TradeCard key={t.transaction_id} trade={t} />
                      ))}
                    </div>
                  </section>
                )}
              </>
            )}
          </>
        )}
      </MainTag>

      {!embedded && (
        <footer className="border-t border-[#0A0A0A] mt-16">
          <div className="max-w-7xl mx-auto px-6 lg:px-12 py-8 text-xs text-[#52525B] font-mono-data flex flex-wrap items-center justify-between gap-2">
            <span>Public view · data from Sleeper + TRYSTOMATIC values</span>
            <span className="flex items-center gap-3">
              <a href="/about" className="hover:text-[#7C3AED] transition-colors">About</a>
              <a href="/privacy" className="hover:text-[#7C3AED] transition-colors">Privacy</a>
              <a href="/support" className="hover:text-[#7C3AED] transition-colors">Support</a>
              <a href="/" className="text-[#7C3AED] hover:underline">Calculator home</a>
            </span>
          </div>
        </footer>
      )}
    </div>
  );
}
